import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { UserXpData, XpHistoryEntry, UserMission, MissionActionRequirement, Mission, Badge as BadgeType, ActiveCooldownInfo } from '../lib/gamification/types';
import { getUserGamificationStatus, getXpHistory, redeemMissionReward, getPendingReferralRewards, claimReferralSignupReward, processAction, getUserActiveCooldowns } from '../lib/gamification/gamificationService';
import { calculateLevelProgress, calculateNextLevelXP } from '../lib/gamification/gamificationUtils';
import { getUserActiveMissions, getUserCompletedMissions } from '../lib/gamification/missionService';
import { getUserProfileBadges } from '../lib/gamification/badgeService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge as UiBadge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, TrendingUp, CalendarDays, History, Star, Hash, Percent, CheckCircle2, Target, Gift, ChevronDown, ChevronUp, Award, Coins, ShieldCheck, Info, Flame, Zap, Copy, CheckCircle, Timer } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'react-hot-toast';
import { getUserStreakStatus, getUserStreakXpMultiplier, registerStreakActivity } from '../lib/gamification/streakService';
import { getUserMascot } from '../lib/gamification/mascotService';
import { UserMascot } from '../lib/gamification/mascotService';

// Tipo explícito para a entrada do histórico, incluindo a relação E os dados enriquecidos
type XpHistoryEntryWithDetails = XpHistoryEntry & {
  xp_actions: { action_key: string, description: string } | null;
  game_slug?: string | null;
  game_name?: string | null;
  source_username?: string | null;
  post_id?: string | null;
};

// Tipo para UserProfileBadge (importado ou definido aqui se UserProfileBadge não for exportado de types.ts)
// Assumindo que UserProfileBadge é exportado de badgeService.ts ou types.ts
type UserProfileBadge = import('../lib/gamification/badgeService').UserProfileBadge;

const XpHistoryPage: React.FC = () => {
  const { user, generateReferralCode } = useAuth();
  const [status, setStatus] = useState<UserXpData | null>(null);
  const [history, setHistory] = useState<XpHistoryEntryWithDetails[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10; // Aumentado para histórico

  const [activeMissions, setActiveMissions] = useState<UserMission[]>([]);
  const [loadingMissions, setLoadingMissions] = useState(true);
  const [expandedMissions, setExpandedMissions] = useState<Record<string, boolean>>({});

  const [completedMissions, setCompletedMissions] = useState<UserMission[]>([]);
  const [loadingCompletedMissions, setLoadingCompletedMissions] = useState(true);
  const [expandedCompletedMissions, setExpandedCompletedMissions] = useState<Record<string, boolean>>({});
  const [redemptionLoading, setRedemptionLoading] = useState<Record<string, boolean>>({});

  // <<< NOVOS ESTADOS PARA BADGES >>>
  const [userBadges, setUserBadges] = useState<UserProfileBadge[]>([]);
  const [loadingUserBadges, setLoadingUserBadges] = useState(true);
  const [activeTab, setActiveTab] = useState("missions"); // Para controlar a aba ativa, se necessário

  // <<< DEFINIÇÃO DA FUNÇÃO getXpSourceLink >>>
  const getXpSourceLink = (entry: XpHistoryEntryWithDetails): string | null => {
    const actionKey = entry.xp_actions?.action_key;

    if (!actionKey) return null;

    // Link para Jogos
    if (['rate_game', 'rate_complexity', 'add_game_to_collection', 'add_game_to_wishlist', 'record_game_session'].includes(actionKey)) {
      if (entry.game_slug) {
        return `/games/${entry.game_slug}`;
      }
    }
    // Link para Perfil de Usuário
    else if (['establish_connection', 'referral_bonus'].includes(actionKey)) {
      if (entry.source_username) {
        return `/profile/${entry.source_username}`;
      }
    }
    // Link para Posts (exemplo, se post_id for enriquecido no futuro)
    else if (actionKey === 'add_comment') {
      if (entry.post_id) {
        return `/posts/${entry.post_id}`; // Ajustar o path conforme a estrutura de rotas de posts
      }
    }
    // Adicionar mais casos conforme necessário

    return null; // Retorna null se nenhum link aplicável for encontrado
  };

  const [pendingReferrals, setPendingReferrals] = useState<any[]>([]);
  const [loadingPendingReferrals, setLoadingPendingReferrals] = useState(true);
  const [claimingReferral, setClaimingReferral] = useState<string | null>(null);

  const [streakStatus, setStreakStatus] = useState<any>(null);
  const [streakXpMultiplier, setStreakXpMultiplier] = useState<number>(1);
  const [loadingStreak, setLoadingStreak] = useState(true);

  const [checkingIn, setCheckingIn] = useState(false);

  const [userMascot, setUserMascot] = useState<UserMascot | null>(null);
  const [loadingMascot, setLoadingMascot] = useState(true);

  // Estados para o código de referência
  const [localReferralCode, setLocalReferralCode] = useState<string | null | undefined>(user?.referral_code);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [copiedReferral, setCopiedReferral] = useState(false);

  // Sincronizar localReferralCode com user.referral_code
  useEffect(() => {
    setLocalReferralCode(user?.referral_code);
  }, [user?.referral_code]);

  const [activeCooldowns, setActiveCooldowns] = useState<ActiveCooldownInfo[]>([]);
  const [loadingCooldowns, setLoadingCooldowns] = useState(true);

  const loadStatus = useCallback(async () => {
    if (!user) return;
    setLoadingStatus(true);
    try {
      const userStatus = await getUserGamificationStatus(supabase, user.id);
      setStatus(userStatus);
    } catch (err: any) {
      console.error("Erro ao carregar status de gamificação:", err);
      setError("Não foi possível carregar seu status de gamificação.");
    } finally {
      setLoadingStatus(false);
    }
  }, [user]);

  const loadActiveMissions = useCallback(async () => {
    if (!user) return;
    setLoadingMissions(true);
    try {
      const missions = await getUserActiveMissions(supabase, user.id);
      setActiveMissions(missions);
    } catch (err: any) {
      console.error("Erro ao carregar missões ativas:", err);
    } finally {
      setLoadingMissions(false);
    }
  }, [user]);

  const loadCompletedMissions = useCallback(async () => {
    if (!user) return;
    setLoadingCompletedMissions(true);
    try {
      const missions = await getUserCompletedMissions(supabase, user.id, 5);
      setCompletedMissions(missions);
    } catch (err: any) {
      console.error("Erro ao carregar missões concluídas:", err);
    } finally {
      setLoadingCompletedMissions(false);
    }
  }, [user]);

  // <<< NOVA FUNÇÃO PARA CARREGAR BADGES DO USUÁRIO >>>
  const loadUserBadges = useCallback(async () => {
    if (!user) return;
    setLoadingUserBadges(true);
    try {
      const badges = await getUserProfileBadges(supabase, user.id);
      const sortedBadges = badges.sort((a, b) => new Date(b.unlocked_at).getTime() - new Date(a.unlocked_at).getTime());
      setUserBadges(sortedBadges.slice(0, 6)); 
    } catch (err: any) {
      console.error("Erro ao carregar badges do usuário:", err);
    } finally {
      setLoadingUserBadges(false);
    }
  }, [user]);

  const loadActiveCooldowns = useCallback(async () => {
    if (!user) return;
    setLoadingCooldowns(true);
    try {
      const cooldowns = await getUserActiveCooldowns(supabase, user.id);
      setActiveCooldowns(cooldowns);
    } catch (err: any) {
      console.error("Erro ao carregar cooldowns ativos:", err);
      if (activeTab === "cooldowns") setError("Não foi possível carregar seus cooldowns ativos.");
    } finally {
      setLoadingCooldowns(false);
    }
  }, [user, supabase, activeTab]);

  const enrichHistoryData = useCallback(async (entries: XpHistoryEntryWithDetails[]): Promise<XpHistoryEntryWithDetails[]> => {
    const enrichedEntries = await Promise.all(
      entries.map(async (entry) => {
        const actionKey = entry.xp_actions?.action_key;
        if (!actionKey) return entry; 

        let enrichedEntry = { ...entry };

        try {
          // Busca game_slug e game_name para ações de jogo
          if (['rate_game', 'rate_complexity', 'add_game_to_collection', 'add_game_to_wishlist', 'record_game_session'].includes(actionKey)) {
            let gameIdToFetch: string | null | undefined = null;

            // Prioridade 1: entry.source_id (especialmente para add_to_collection/wishlist)
            if (actionKey === 'add_game_to_collection' || actionKey === 'add_game_to_wishlist') {
              gameIdToFetch = entry.source_id;
            }

            // Prioridade 2: entry.details.gameId (para outras ações de jogo como rate_game, record_session)
            // ou como fallback se source_id não for o gameId esperado.
            if (!gameIdToFetch && typeof entry.details === 'object' && entry.details !== null && 'gameId' in entry.details) {
              gameIdToFetch = entry.details.gameId as string;
            }
            
            if (gameIdToFetch) { 
              const { data: gameData, error: gameError } = await supabase
                .from('games')
                .select('slug, name')
                .eq('id', gameIdToFetch) 
                .maybeSingle();
              if (gameError) console.error(`Erro ao buscar slug/nome para game ${gameIdToFetch}:`, gameError.message);
              if (gameData) {
                enrichedEntry.game_slug = gameData.slug;
                enrichedEntry.game_name = gameData.name;
              }
            } else {
                 console.warn(`[enrichHistoryData] Não foi possível determinar o gameId para a entrada ${entry.id} (actionKey: ${actionKey})`);
            }
          }
          // Busca source_username
          else if (['establish_connection', 'referral_bonus'].includes(actionKey)) {
             // Verifica se source_id existe antes de buscar
             if (entry.source_id) {
                 const { data: profileData, error: profileError } = await supabase
                   .from('profiles')
                   .select('username')
                   .eq('id', entry.source_id)
                   .maybeSingle();
                 if (profileError) console.error(`Erro ao buscar username para profile ${entry.source_id}:`, profileError.message);
                 if (profileData) enrichedEntry.source_username = profileData.username;
             } else {
                 console.warn(`[enrichHistoryData] source_id faltando para a entrada ${entry.id} (actionKey: ${actionKey})`);
             }
          }
           // Busca post_id para comentários (assumindo source_id é comment_id)
           else if (actionKey === 'add_comment') {
             // Precisamos encontrar o post_id a partir do comment_id
             // Isso pode requerer uma busca na tabela 'comments' (se existir) ou uma RPC
             // Exemplo (se houver tabela 'comments'):
             /*
             const { data: commentData, error: commentError } = await supabase
               .from('comments') // Substituir pelo nome real da tabela
               .select('post_id')
               .eq('id', entry.source_id)
               .maybeSingle();
             if (commentError) console.error(`Erro ao buscar post_id para comment ${entry.source_id}:`, commentError.message);
             if (commentData) enrichedEntry.post_id = commentData.post_id;
             */
             // Por ora, mantemos sem post_id para comentários
           }

        } catch (enrichError) {
            console.error(`Erro ao enriquecer entrada ${entry.id} (${actionKey}):`, enrichError);
        }
        return enrichedEntry;
      })
    );
    return enrichedEntries;
  }, [supabase]);

  const loadHistory = useCallback(async (pageNum: number) => {
    if (!user) return;
    setLoadingHistory(true);
    // Não limpar o erro global aqui para não afetar outras abas
    // setError(null); 
    try {
      const { data, count } = await getXpHistory(supabase, user.id, pageNum, pageSize);
      const enrichedData = await enrichHistoryData(data);
      setHistory(prev => pageNum === 1 ? enrichedData : [...prev, ...enrichedData]);
      setTotalPages(Math.ceil((count || 0) / pageSize));
    } catch (err: any) {
      console.error("Erro ao carregar histórico de XP:", err);
      // Definir um erro específico para o histórico se necessário
      if (activeTab === "history") setError("Não foi possível carregar seu histórico de XP.");
      if (pageNum === 1) setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [user, pageSize, enrichHistoryData, activeTab]); // Adicionar activeTab

  // useEffect para carregar o status inicial do usuário e quando o usuário mudar
  useEffect(() => {
    loadStatus();
  }, [loadStatus]); // loadStatus já depende de 'user'

  // useEffect para carregar dados das abas (missões, badges, histórico)
  useEffect(() => {
    if (activeTab === "missions") {
      // Carrega missões se ainda não foram carregadas ou se o usuário mudou
      // As funções de load já são useCallbacks dependentes de 'user'
      loadActiveMissions();
      loadCompletedMissions();
    } else if (activeTab === "badges") {
      loadUserBadges();
    } else if (activeTab === "history") {
      // Carrega o histórico da página 1 APENAS se a aba se tornou ativa E o histórico está vazio.
      // A paginação subsequente é tratada por handleLoadMoreHistory.
      if (history.length === 0) {
        setPage(1); // Garante que a lógica de paginação comece da página 1
        loadHistory(1);
      }
    } else if (activeTab === "cooldowns") {
      loadActiveCooldowns();
    }
    // As dependências aqui são as funções de carregamento (que são stables devido ao useCallback e dependem de user)
    // e activeTab para re-trigger ao mudar de aba.
    // O user não é necessário aqui diretamente porque as funções de load já o têm como dependência.
  }, [activeTab, loadActiveMissions, loadCompletedMissions, loadUserBadges, loadHistory, loadActiveCooldowns, history.length]);

  const handleLoadMoreHistory = () => {
    if (page < totalPages && !loadingHistory) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadHistory(nextPage);
    }
  };

  const renderHistoryRow = (entry: XpHistoryEntryWithDetails) => {
    const earnedAt = formatDistanceToNow(new Date(entry.earned_at), { addSuffix: true, locale: ptBR });
    const sourceLink = getXpSourceLink(entry);

    return (
      <TableRow key={entry.id}>
        <TableCell className="font-medium min-w-[150px] break-words">
          {entry.xp_actions?.description || entry.source_type}
        </TableCell>
        <TableCell className="min-w-[50px]">{entry.amount > 0 ? `+${entry.amount}` : entry.amount}</TableCell>
        <TableCell className="hidden sm:table-cell">
          {sourceLink ? (
            <Link to={sourceLink} className="text-blue-600 hover:underline">
              Ver Fonte
            </Link>
          ) : (
            <span className="text-gray-500">-</span>
          )}
        </TableCell>
        <TableCell className="text-right min-w-[100px]">{earnedAt}</TableCell>
        <TableCell className="min-w-[120px] break-words hidden sm:table-cell">{entry.game_name || '-'}</TableCell>
      </TableRow>
    );
  };

  // Carregar indicações pendentes
  const loadPendingReferrals = useCallback(async () => {
    if (!user) return;
    setLoadingPendingReferrals(true);
    try {
      const referrals = await getPendingReferralRewards(supabase, user.id);
      setPendingReferrals(referrals);
    } catch (err) {
      console.error('Erro ao buscar indicações pendentes:', err);
    } finally {
      setLoadingPendingReferrals(false);
    }
  }, [user]);

  useEffect(() => {
    loadPendingReferrals();
  }, [loadPendingReferrals]);

  // Função para coletar recompensa de indicação
  const handleClaimReferral = async (referralId: string) => {
    if (!user) return;
    setClaimingReferral(referralId);
    try {
      const result = await claimReferralSignupReward(supabase, user.id, referralId);
      if (result.success) {
        toast.success('Recompensa de indicação coletada! +XP');
        setPendingReferrals(prev => prev.filter(r => r.id !== referralId));
        loadStatus(); // Atualiza XP/nível
        loadHistory(1); // Atualiza histórico
      } else {
        toast.error(result.error || 'Erro ao coletar recompensa.');
      }
    } catch (err) {
      toast.error('Erro inesperado ao coletar recompensa.');
    } finally {
      setClaimingReferral(null);
    }
  };

  // Carregar status do streak
  const loadStreakStatus = useCallback(async () => {
    if (!user) return;
    setLoadingStreak(true);
    try {
      const status = await getUserStreakStatus(supabase, user.id);
      setStreakStatus(status);
      const multiplier = await getUserStreakXpMultiplier(supabase, user.id);
      setStreakXpMultiplier(multiplier);
    } catch (err) {
      console.error('Erro ao carregar status de streak:', err);
    } finally {
      setLoadingStreak(false);
    }
  }, [user]);

  useEffect(() => {
    loadStreakStatus();
  }, [loadStreakStatus]);

  // Função para fazer check-in diário
  const handleDailyCheckIn = async () => {
    if (!user) return;
    setCheckingIn(true);
    try {
      // 1. Registrar streak
      const streakResult = await registerStreakActivity(supabase, user.id, 'login');
      // 2. Processar ação de XP (daily_login)
      const xpResult = await processAction({
        supabase,
        userId: user.id,
        actionKey: 'daily_login'
      });

      if (streakResult.success && xpResult.success) {
        toast.success(`Check-in diário realizado com sucesso! ${xpResult.xpGained ? `+${xpResult.xpGained} XP` : ''}`);
        loadStreakStatus();
        loadStatus(); // Atualiza XP/nível
        loadHistory(1); // Atualiza histórico
      } else {
        // Prioriza a mensagem de erro da xpResult se houver, senão da streakResult
        const errorMessage = xpResult.error || streakResult.error || 'Erro ao registrar check-in.';
        toast.error(errorMessage);
      }
    } catch (err) {
      toast.error('Erro inesperado ao registrar check-in.');
    } finally {
      setCheckingIn(false);
    }
  };

  // Log para depuração do status do streak
  console.log('streakStatus:', streakStatus);

  // Carregar mascote do usuário
  const loadUserMascot = useCallback(async () => {
    if (!user) return;
    setLoadingMascot(true);
    try {
      const mascot = await getUserMascot(supabase, user.id, status?.current_level ?? 1);
      setUserMascot(mascot);
    } catch (err) {
      setUserMascot(null);
    } finally {
      setLoadingMascot(false);
    }
  }, [user, status]);

  useEffect(() => {
    loadUserMascot();
  }, [loadUserMascot]);

  const handleGenerateReferralCode = async () => {
    if (!user || !generateReferralCode) return;
    setIsGeneratingCode(true);
    setGenerateError(null);
    try {
      const result = await generateReferralCode(); // Usar generateReferralCode do useAuth
      if (result.success && result.code) {
        setLocalReferralCode(result.code);
        // A atualização do user.referral_code deve ser feita pelo hook useAuth internamente
        // Se não for, precisaremos atualizar o estado 'user' aqui ou recarregar o usuário.
        // Por simplicidade, assumimos que o hook useAuth atualiza o 'user' globalmente.
        toast.success('Seu código de indicação foi gerado!');
      } else {
        setGenerateError(result.error || 'Falha ao gerar o código.');
        toast.error(result.error || 'Falha ao gerar o código de indicação.');
      }
    } catch (error: any) {
      setGenerateError(error.message || 'Erro desconhecido ao gerar código.');
      toast.error(error.message || 'Erro desconhecido ao gerar código de indicação.');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleCopyReferralLink = () => {
    if (!localReferralCode) return;
    const referralLink = `${window.location.origin}/signup?ref=${localReferralCode}`;
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setCopiedReferral(true);
        toast.success('Link de indicação copiado!');
        setTimeout(() => setCopiedReferral(false), 2000);
      })
      .catch(err => {
        console.error('Erro ao copiar link de indicação:', err);
        toast.error('Erro ao copiar o link de indicação.');
      });
  };

  // Função para formatar o tempo restante em milissegundos
  const formatRemainingTime = (ms: number): string => {
    if (ms <= 0) return "Expirado";
    const totalSeconds = Math.ceil(ms / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 && (parts.length === 0 || (days === 0 && hours === 0 && minutes === 0))) {
      parts.push(`${seconds}s`);
    }
    
    if (parts.length === 0 && totalSeconds > 0) {
        return `${totalSeconds.toFixed(1)}s`;
    }
    if (parts.length === 0 && totalSeconds <=0) {
        return "Expirado";
    }

    return parts.join(' ') || "Breve";
  };

  if (error) {
    return <div className="container mx-auto p-4 text-destructive">{error}</div>;
  }

  // Função para formatar a descrição da ação da missão (pode ser expandida)
  const formatMissionActionDescription = (action: MissionActionRequirement): string => {
    // TODO: Mapear action.action_type para uma descrição mais amigável se necessário
    // Ex: 'create_post' -> 'Criar posts'
    let desc = `Realizar '${action.action_type}' ${action.quantity} vez(es).`;
    if (action.params) {
      if (action.params.unique_entity) {
        desc += " (entidades únicas)";
      }
      if (action.params.category) {
        desc += ` (categoria: ${action.params.category})`;
      }
      // Adicionar mais formatação de params conforme necessário
    }
    return desc;
  };
  
  const toggleMissionExpand = (missionId: string) => {
    setExpandedMissions(prev => ({
      ...prev,
      [missionId]: !prev[missionId]
    }));
  };

  // <<< NOVA FUNÇÃO PARA EXPANDIR MISSÕES CONCLUÍDAS >>>
  const toggleCompletedMissionExpand = (missionId: string) => {
    setExpandedCompletedMissions(prev => ({
      ...prev,
      [missionId]: !prev[missionId]
    }));
  };

  // <<< NOVA FUNÇÃO PARA RESGATAR RECOMPENSA DA MISSÃO >>>
  const handleRedeemReward = async (userMissionId: string, missionTitle: string) => {
    if (!user) {
      toast.error("Você precisa estar logado para resgatar recompensas.");
      return;
    }
    setRedemptionLoading(prev => ({ ...prev, [userMissionId]: true }));
    try {
      const result = await redeemMissionReward(supabase, user.id, userMissionId);
      if (result.success) {
        let rewardMessage = `Recompensa da missão "${missionTitle}" resgatada!`;
        if (result.xp_granted) rewardMessage += ` (+${result.xp_granted} XP)`;
        if (result.badge_id_granted) rewardMessage += ", Badge Conquistada!";
        toast.success(rewardMessage);
        setCompletedMissions(prevMissions =>
          prevMissions.map(um =>
            um.id === userMissionId ? { ...um, is_rewarded: true } : um
          )
        );
        loadStatus(); // Recarregar status geral
        loadUserBadges(); // Recarregar badges caso uma nova tenha sido concedida pela missão
      } else {
        toast.error(result.error || "Falha ao resgatar recompensa.");
      }
    } catch (error: any) {
      console.error("Erro ao resgatar recompensa da missão:", error);
      toast.error("Ocorreu um erro inesperado ao tentar resgatar a recompensa.");
    } finally {
      setRedemptionLoading(prev => ({ ...prev, [userMissionId]: false }));
    }
  };

  // Função para renderizar um card de missão (pode ser reutilizada para ativas e concluídas)
  const renderMissionCard = (userMission: UserMission, type: 'active' | 'completed') => {
    if (!userMission.mission) return null;
    const mission = userMission.mission;
    const isExpanded = type === 'active' ? !!expandedMissions[userMission.id] : !!expandedCompletedMissions[userMission.id];
    const toggleExpand = type === 'active' ? toggleMissionExpand : toggleCompletedMissionExpand;
    const isLoadingRedemption = type === 'completed' ? (redemptionLoading[userMission.id] || false) : false;

    let overallProgressPercent = 0;
    let completedActions = 0;
    const totalActions = mission.required_actions.length;

    mission.required_actions.forEach(reqAction => {
      const currentProgress = userMission.progress?.[reqAction.action_type]?.count || 0;
      if (currentProgress >= reqAction.quantity) {
        completedActions++;
      }
    });
    if (totalActions > 0) {
      overallProgressPercent = (completedActions / totalActions) * 100;
    }
    
    const cardBgClass = type === 'completed' 
      ? (userMission.is_rewarded ? 'bg-gray-100 dark:bg-gray-600 opacity-70' : 'bg-green-50 dark:bg-green-900/30')
      : 'bg-slate-50 dark:bg-slate-800/50';
    
    const titleColorClass = type === 'completed'
      ? (userMission.is_rewarded ? 'text-gray-600 dark:text-gray-400' : 'text-green-700 dark:text-green-300')
      : 'text-slate-800 dark:text-slate-200';

    let rewardButtonText = "Resgatar";
    if (type === 'completed') {
        const rewardsPending: string[] = [];
        if (mission.xp_reward && mission.xp_reward > 0) rewardsPending.push(`${mission.xp_reward} XP`);
        if (mission.badge_reward_id) rewardsPending.push("Badge");
        if (rewardsPending.length > 0) rewardButtonText = `Resgatar (${rewardsPending.join(' & ')})`;
    }

    return (
      <div key={userMission.id} className={`p-4 border rounded-lg transition-all duration-300 ${cardBgClass}`}>
        <div className="flex justify-between items-start">
          <div onClick={() => toggleExpand(userMission.id)} className="flex-grow cursor-pointer mr-2">
            <h3 className={`font-semibold text-lg flex items-center ${titleColorClass}`}>
              {type === 'completed' && (
                <CheckCircle2 className={`h-5 w-5 mr-2 ${userMission.is_rewarded ? 'text-gray-500' : 'text-green-600 dark:text-green-400'}`}/>
              )}
              {mission.title}
            </h3>
            {isExpanded && (
                <p className={`text-sm mt-1 mb-2 ${type === 'completed' && userMission.is_rewarded ? 'text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>
                    {mission.description}
                </p>
            )}
          </div>
          <div className="flex flex-col items-end flex-shrink-0">
             <Button variant="ghost" size="sm" onClick={() => toggleExpand(userMission.id)} className="p-1 h-auto self-end mb-1">
                {isExpanded ? <ChevronUp className="h-5 w-5 text-muted-foreground"/> : <ChevronDown className="h-5 w-5 text-muted-foreground"/>}
            </Button>
            {type === 'active' && (
              <>
                {mission.xp_reward > 0 && (
                  <UiBadge variant="outline" className="text-xs mb-1">
                    <Star className="h-3 w-3 mr-1 text-yellow-500"/> {mission.xp_reward} XP
                  </UiBadge>
                )}
                {mission.badge_reward_id && (
                   <UiBadge variant="outline" className="text-xs">
                     <Gift className="h-3 w-3 mr-1 text-purple-500"/> Badge
                   </UiBadge>
                )}
              </>
            )}
            {type === 'completed' && (
                userMission.is_rewarded ? (
                  <UiBadge variant="secondary" className="text-xs mt-1">
                    <Award className="h-3 w-3 mr-1 text-blue-500"/> Recompensa Coletada
                  </UiBadge>
                ) : (
                  <div className="flex flex-col items-end mt-1">
                    {mission.xp_reward > 0 && (
                      <UiBadge variant="outline" className="text-xs mb-1 border-yellow-400 text-yellow-600 dark:text-yellow-300">
                        <Star className="h-3 w-3 mr-1 text-yellow-500"/> +{mission.xp_reward} XP
                      </UiBadge>
                    )}
                    {mission.badge_reward_id && (
                       <UiBadge variant="outline" className="text-xs border-purple-400 text-purple-600 dark:text-purple-300">
                         <Gift className="h-3 w-3 mr-1 text-purple-500"/> Badge Pendente
                       </UiBadge>
                    )}
                  </div>
                )
            )}
          </div>
        </div>

        {type === 'active' && (
            <>
                <Progress value={overallProgressPercent} className="w-full h-2 mt-2 mb-1" />
                <p className="text-xs text-muted-foreground text-right mb-2">{completedActions} de {totalActions} objetivos completos</p>
            </>
        )}

        {isExpanded && type === 'active' && mission.required_actions && mission.required_actions.length > 0 && (
          <div className="mt-3 space-y-3 pl-2 border-l-2 border-slate-200 dark:border-slate-700">
            {mission.required_actions.map((reqAction, index) => {
              const currentProgress = userMission.progress?.[reqAction.action_type]?.count || 0;
              const progressPercent = Math.min((currentProgress / reqAction.quantity) * 100, 100);
              return (
                <div key={`${userMission.id}-active-${reqAction.action_type}-${index}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center text-slate-700 dark:text-slate-300">
                      {currentProgress >= reqAction.quantity ? <CheckCircle2 className="h-4 w-4 mr-2 text-green-500"/> : <Target className="h-4 w-4 mr-2 text-slate-500"/>}
                      {formatMissionActionDescription(reqAction)}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {currentProgress}/{reqAction.quantity}
                    </span>
                  </div>
                  <Progress value={progressPercent} className="w-full h-1 mt-1" />
                </div>
              );
            })}
          </div>
        )}

        {type === 'completed' && !userMission.is_rewarded && userMission.is_complete && (
            <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700/50">
                <Button
                    onClick={() => handleRedeemReward(userMission.id, mission.title)}
                    disabled={isLoadingRedemption}
                    size="sm"
                    className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                    {isLoadingRedemption ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                        <Coins className="mr-2 h-4 w-4" />
                    )}
                    {isLoadingRedemption ? 'Resgatando...' : rewardButtonText}
                </Button>
            </div>
        )}
        
        {isExpanded && userMission.expires_at && type === 'active' && (
            <p className="text-xs text-amber-600 dark:text-amber-500 mt-3">
                Expira {formatDistanceToNow(new Date(userMission.expires_at), { addSuffix: true, locale: ptBR })}
            </p>
        )}
        {isExpanded && userMission.completed_at && type === 'completed' && (
            <p className={`text-xs mt-3 ${userMission.is_rewarded ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                Concluída {formatDistanceToNow(new Date(userMission.completed_at), { addSuffix: true, locale: ptBR })}
            </p>
        )}
      </div>
    );
  };

  // Seção de indicações pendentes
  const renderPendingReferrals = () => (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Programa de Indicação</CardTitle>
        <CardDescription>Convide amigos e ganhe recompensas. Veja abaixo as recompensas pendentes por usuários que você já indicou.</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Seção para Gerar/Copiar Código de Indicação */}
        <div className="mb-6 p-4 border border-dashed border-indigo-300 rounded-lg bg-indigo-50 dark:bg-indigo-900/30">
          <h4 className="text-md font-semibold text-indigo-700 dark:text-indigo-300 mb-3 flex items-center">
            <Gift className="h-5 w-5 mr-2" />
            Meu Código de Indicação
          </h4>
          {localReferralCode ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Compartilhe seu link de indicação com amigos!
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-2">
                <span className="text-lg font-mono text-indigo-700 dark:text-indigo-300 tracking-wider bg-white dark:bg-slate-700 px-3 py-1.5 rounded border border-indigo-200 dark:border-indigo-600 select-all flex-grow text-center sm:text-left">
                  {localReferralCode}
                </span>
                <Button
                  onClick={handleCopyReferralLink}
                  variant="outline"
                  size="sm"
                  className="bg-white dark:bg-slate-600 border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-slate-500 w-full sm:w-auto"
                >
                  {copiedReferral ? <CheckCircle className="mr-2 h-4 w-4 text-green-500" /> : <Copy className="mr-2 h-4 w-4" />}
                  {copiedReferral ? 'Copiado!' : 'Copiar Link'}
                </Button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Seu link: <code className="bg-gray-100 dark:bg-gray-700 p-1 rounded text-xs">{`${window.location.origin}/signup?ref=${localReferralCode}`}</code>
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Você ainda não gerou seu código. Clique abaixo para criar o seu!
              </p>
              <Button
                onClick={handleGenerateReferralCode}
                disabled={isGeneratingCode || !user || !generateReferralCode}
                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
              >
                {isGeneratingCode ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Gift className="mr-2 h-4 w-4" />
                )}
                {isGeneratingCode ? 'Gerando Código...' : 'Gerar Meu Código'}
              </Button>
              {generateError && (
                <p className="text-sm text-red-600 mt-1">{generateError}</p>
              )}
            </div>
          )}
        </div>

        {/* Lista de Indicações Pendentes */}
        <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">Recompensas Pendentes</h4>
        {loadingPendingReferrals ? (
          <div className="flex justify-center items-center h-12"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : pendingReferrals.length === 0 ? (
          <p className="text-muted-foreground text-center">Nenhuma recompensa de indicação pendente.</p>
        ) : (
          <div className="space-y-3">
            {pendingReferrals.map(ref => (
              <div key={ref.id} className="flex items-center justify-between border rounded p-3">
                <div className="font-medium flex items-center gap-2">
                  Usuário indicado:
                  {ref.profiles?.avatar_url && (
                    <img src={ref.profiles.avatar_url} alt={ref.profiles.username} className="w-6 h-6 rounded-full inline-block" />
                  )}
                  <span className="font-mono">{ref.profiles?.username || ref.referred_id}</span>
                  {ref.profiles?.full_name && (
                    <span className="text-xs text-muted-foreground ml-2">({ref.profiles.full_name})</span>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => handleClaimReferral(ref.id)}
                  disabled={claimingReferral === ref.id}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {claimingReferral === ref.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Coletar recompensa'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Card de streak
  const renderStreakCard = () => (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-orange-500" />
          Streak Diário
        </CardTitle>
        <CardDescription>Mantenha sua sequência de logins diários para ganhar mais XP!</CardDescription>
      </CardHeader>
      <CardContent>
        {loadingStreak ? (
          <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-primary" /> Carregando streak...</div>
        ) : (
          <div>
            {/* Botão de check-in para o primeiro acesso (streakStatus null) */}
            {!streakStatus && (
              <div className="mb-4 flex flex-col items-start">
                <span className="text-orange-700 font-semibold mb-2">Comece seu streak diário agora!</span>
                <Button size="sm" onClick={handleDailyCheckIn} disabled={checkingIn} className="bg-orange-500 hover:bg-orange-600 text-white">
                  {checkingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fazer check-in diário'}
                </Button>
              </div>
            )}
            {/* Se existe streak, renderizar o restante normalmente */}
            {streakStatus && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-lg font-semibold">
                    <Flame className="h-5 w-5 text-orange-500" />
                    {streakStatus.current_streak} dias
                  </span>
                  <span className="text-xs text-muted-foreground">Maior streak: {streakStatus.longest_streak} dias</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-green-600 font-semibold">
                    <Zap className="h-4 w-4" />
                    Multiplicador XP: x{streakXpMultiplier.toFixed(2)}
                  </span>
                  {streakStatus.weekly_protection_available ? (
                    <span className="flex items-center gap-1 text-blue-600" title="Proteção semanal disponível">
                      <ShieldCheck className="h-4 w-4" /> Proteção semanal disponível
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-gray-400" title="Proteção semanal já usada nesta semana">
                      <ShieldCheck className="h-4 w-4" /> Proteção usada
                    </span>
                  )}
                  {/* Botão de check-in diário (lógica robusta para streak existente) */}
                  {(() => {
                    if (!streakStatus.last_activity_date) {
                      return (
                        <div className="flex items-start">
                          <Button size="sm" onClick={handleDailyCheckIn} disabled={checkingIn} className="bg-orange-500 hover:bg-orange-600 text-white">
                            {checkingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fazer check-in diário'}
                          </Button>
                        </div>
                      );
                    }
                    const todayStr = new Date().toISOString().slice(0, 10);
                    const lastStr = new Date(streakStatus.last_activity_date).toISOString().slice(0, 10);
                    if (todayStr !== lastStr) {
                      return (
                        <div className="flex items-start">
                          <Button size="sm" onClick={handleDailyCheckIn} disabled={checkingIn} className="bg-orange-500 hover:bg-orange-600 text-white">
                            {checkingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fazer check-in diário'}
                          </Button>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Card de mascote
  const renderMascotCard = () => (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span role="img" aria-label="Mascote">🐾</span> Mascote
        </CardTitle>
        <CardDescription>Escolha um mascote para te acompanhar e evoluir conforme seu progresso!</CardDescription>
      </CardHeader>
      <CardContent>
        {loadingMascot ? (
          <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-primary" /> Carregando mascote...</div>
        ) : userMascot ? (
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <img src={userMascot.current_level.image_url} alt={userMascot.mascot.name} className="w-16 h-16 rounded-full object-cover border" />
            <div className="text-center sm:text-left">
              <div className="font-semibold text-lg">{userMascot.mascot.name}</div>
              <div className="text-sm text-muted-foreground">Nível {userMascot.current_level.level_number}</div>
            </div>
            <Button asChild variant="outline" className="w-full mt-2 sm:w-auto sm:mt-0 sm:ml-auto">
              <Link to="/profile/choose-mascot">Trocar mascote</Link>
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-2">
            <span className="text-orange-700 font-semibold">Você ainda não escolheu um mascote!</span>
            <Button asChild variant="default">
              <Link to="/profile/choose-mascot">Escolher mascote</Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-4 space-y-6">
      <h1 className="text-2xl font-bold">Meu Progresso</h1>

      {/* Card de status atual */}
      {loadingStatus ? (
        <Card>
          <CardHeader><CardTitle>Status Atual</CardTitle><CardDescription>Carregando...</CardDescription></CardHeader>
          <CardContent className="flex justify-center items-center h-40"><Loader2 className="h-8 w-8 animate-spin text-primary" /></CardContent>
        </Card>
      ) : status ? (
        <Card>
          <CardHeader><CardTitle>Status Atual</CardTitle><CardDescription>Seu nível e progresso na plataforma.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                <span className="font-semibold">Nível {status.current_level}</span>
                <UiBadge variant="secondary">{status.current_rank?.toUpperCase()}</UiBadge>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                {status.rank_position !== null && (<div className="flex items-center gap-1" title="Sua posição no ranking global"><Hash className="h-4 w-4" /><span>#{status.rank_position}</span></div>)}
                {status.percentile !== null && (<div className="flex items-center gap-1" title={`Você está acima de ${status.percentile?.toFixed(1)}% dos jogadores`}> <TrendingUp className="h-4 w-4" /> <span>Top {(100 - (status.percentile || 0))?.toFixed(1)}%</span></div>)}
              </div>
            </div>
            <div>
              <Progress value={calculateLevelProgress(status.total_xp, status.current_level)} className="w-full" />
              <div className="flex justify-between mt-1">
                 <span className="text-sm text-muted-foreground">{status.total_xp} XP Total</span>
                 <p className="text-sm text-muted-foreground">Próximo nível: {calculateNextLevelXP(status.current_level)} XP</p>
               </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card><CardHeader><CardTitle>Status Atual</CardTitle></CardHeader><CardContent><p className="text-muted-foreground">Não foi possível carregar seu status.</p></CardContent></Card>
      )}

      {/* Card de streak diário */}
      {renderStreakCard()}

      {/* Indicações pendentes */}
      {renderPendingReferrals()}

      {/* Card de mascote */}
      {renderMascotCard()}

      {/* Tabs de missões, badges, histórico e cooldowns */}
      <Tabs defaultValue="missions" className="w-full" onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto">
          <TabsTrigger value="missions">Missões</TabsTrigger>
          <TabsTrigger value="badges">Badges</TabsTrigger>
          <TabsTrigger value="history">Histórico de XP</TabsTrigger>
          <TabsTrigger value="cooldowns">Cooldowns</TabsTrigger>
        </TabsList>

        <TabsContent value="missions" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="flex items-center mb-2 sm:mb-0">
                  Missões Ativas
                  {activeMissions.length > 0 && <UiBadge variant="secondary" className="ml-2">{activeMissions.length}</UiBadge>}
                </CardTitle>
                <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                  <Link to="/profile/missions">Ver Todas as Missões</Link>
                </Button>
              </div>
              <CardDescription className="mt-2 sm:mt-1">Complete missões para ganhar recompensas!</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingMissions ? (
                <div className="flex justify-center items-center h-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : activeMissions.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Você não possui missões ativas no momento.</p>
              ) : (
                <div className="space-y-4">
                  {activeMissions.map(um => renderMissionCard(um, 'active'))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="mt-6">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="mb-2 sm:mb-0">Missões Concluídas Recentemente</CardTitle>
                <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                  <Link to="/profile/missions">Ver Todas as Missões</Link>
                </Button>
              </div>
              <CardDescription className="mt-2 sm:mt-1">Suas últimas missões finalizadas.</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingCompletedMissions ? (
                <div className="flex justify-center items-center h-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : completedMissions.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Você ainda não completou nenhuma missão recentemente.</p>
              ) : (
                <div className="space-y-4">
                  {completedMissions.map(um => renderMissionCard(um, 'completed'))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="badges" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Badges Conquistadas Recentemente</CardTitle>
              <CardDescription>Uma amostra das suas badges. Veja todas na sua página de badges!</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingUserBadges ? (
                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {Array.from({ length: 6 }).map((_, index) => (
                        <div key={index} className="flex flex-col items-center space-y-2 p-3 border rounded-lg">
                            <Skeleton className="h-16 w-16 rounded-full" />
                            <Skeleton className="h-4 w-3/4" />
                            <Skeleton className="h-3 w-1/2" />
                        </div>
                    ))}
                </div>
              ) : userBadges.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Você ainda não conquistou nenhuma badge. Continue explorando e interagindo!</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {userBadges.map((badge) => (
                    <div key={badge.id} className="flex flex-col items-center text-center p-3 border rounded-lg hover:shadow-md transition-shadow bg-gray-50 dark:bg-gray-800/50" title={badge.description}>
                      {badge.icon_url ? (
                        <img src={badge.icon_url} alt={badge.name} className="w-16 h-16 mb-2 rounded-full object-cover" />
                      ) : (
                        <div className="w-16 h-16 mb-2 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <ShieldCheck className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                        </div>
                      )}
                      <p className="font-semibold text-sm text-gray-800 dark:text-gray-200">{badge.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(badge.unlocked_at), { addSuffix: true, locale: ptBR })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-6 text-center">
                <Button asChild variant="default">
                  <Link to="/my-badges">Ver Todas as Minhas Badges</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Experiência</CardTitle>
              <CardDescription>Registro detalhado de como você ganhou XP na plataforma.</CardDescription>
            </CardHeader>
            <CardContent>
              {error && activeTab === "history" && <p className="text-red-500 mb-4">{error}</p>}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[150px]">Ação</TableHead>
                      <TableHead className="min-w-[50px]">XP</TableHead>
                      <TableHead className="hidden sm:table-cell">Detalhes</TableHead>
                      <TableHead className="text-right min-w-[100px]">Data</TableHead>
                      <TableHead className="min-w-[120px] hidden sm:table-cell">Jogo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingHistory && history.length === 0 ? ( // Mostrar skeletons apenas no load inicial da aba
                      Array.from({ length: pageSize }).map((_, index) => (
                        <TableRow key={`skeleton-hist-${index}`}>
                          <TableCell className="min-w-[150px]"><Skeleton className="h-4 w-full" /></TableCell>
                          <TableCell className="min-w-[50px]"><Skeleton className="h-4 w-full" /></TableCell>
                          <TableCell className="hidden sm:table-cell"><Skeleton className="h-4 w-full" /></TableCell>
                          <TableCell className="text-right min-w-[100px]"><Skeleton className="h-4 w-full" /></TableCell>
                          <TableCell className="min-w-[120px] hidden sm:table-cell"><Skeleton className="h-4 w-full" /></TableCell>
                        </TableRow>
                      ))
                    ) : history.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                          Nenhum histórico de XP encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {history.map(renderHistoryRow)}
                        {page < totalPages && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-4">
                              <Button onClick={handleLoadMoreHistory} variant="outline" disabled={loadingHistory}>
                                {loadingHistory ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Carregando...</> : 'Carregar mais histórico'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cooldowns" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Cooldowns Ativos</CardTitle>
              <CardDescription>Ações que estão temporariamente indisponíveis para ganho de XP.</CardDescription>
            </CardHeader>
            <CardContent>
              {error && activeTab === "cooldowns" && <p className="text-red-500 mb-4">{error}</p>} 
              {loadingCooldowns ? (
                <div className="flex justify-center items-center h-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : activeCooldowns.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Nenhum cooldown ativo no momento.</p>
              ) : (
                <div className="space-y-4">
                  {activeCooldowns.map((cd) => (
                    <div key={cd.action_id} className="p-4 border rounded-lg bg-amber-50 dark:bg-amber-900/30">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                        <h4 className="font-semibold text-md text-amber-700 dark:text-amber-300 flex items-center">
                          <Timer className="h-5 w-5 mr-2" />
                          {cd.action_description}
                        </h4>
                        <UiBadge variant="outline" className="mt-1 sm:mt-0 text-xs border-amber-400 text-amber-600 dark:text-amber-300">
                          Ação: {cd.action_key}
                        </UiBadge>
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground space-y-1 pl-7 sm:pl-0">
                        <p>
                          Tempo restante: <span className="font-semibold text-amber-600 dark:text-amber-400">{formatRemainingTime(cd.remaining_time_ms)}</span>
                        </p>
                        <p>
                          Expira em: <span className="font-semibold">{format(new Date(cd.cooldown_expires_at), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}</span>
                        </p>
                        <p>
                          Última execução: <span className="font-semibold">{format(new Date(cd.last_executed_at), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default XpHistoryPage; 